import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleUp, faAngleUp, faAngleDown, faAngleDoubleDown } from "@fortawesome/free-solid-svg-icons";
import { HOST } from "../../common/constant";
import axiosInstance from "../../common/interceptor";
import { toastOptions } from "../../common/Utils";
import { BOARD_COLUMNS, orderColumns } from "./AsphaltDashboard";
import "./AsphaltDashboard.css";

type BoardColumn = { key: string; label: string };

// 設計「台目・端末名: チェック有の状態で非活性で表示する」→ 常に表示、チェック固定・非活性。
const ALWAYS_SHOWN = ["daiMe", "terminalName"];

/**
 * 表示項目設定: ボードの各カラムを表示/非表示 + 並べ替え。
 * 設計「ぴたあっとWeb端末一覧表示項目設定と同じ仕様」: 行を選択し、先頭/ひとつ上/ひとつ下/最後 で移動。
 * - 非表示カラム → ASP_DISPLAY_ITEMS(CSV)  ・ 並び順 → ASP_DISPLAY_ORDER(CSV)  ※SKY-77
 * - 台目・端末名 は非表示にできない（チェック非活性）。
 */
const DisplayItemSetting: React.FC = () => {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [cols, setCols] = useState<BoardColumn[]>([...BOARD_COLUMNS]);   // SKY-77: 並び順を保持
  const [selected, setSelected] = useState<string | null>(null);        // 選択中の項目 key
  const [keep, setKeep] = useState<{ workStart: string; dateFormat: string }>({ workStart: "", dateFormat: "time" });

  useEffect(() => {
    axiosInstance
      .get(HOST() + "/Form/V1/Dat/AsphaltDashboard/Setting")
      .then((res) => {
        const d = res.data?.data as
          { displayItems?: string; workStart?: string; dateFormat?: string; displayOrder?: string } | undefined;
        setKeep({ workStart: d?.workStart ?? "", dateFormat: d?.dateFormat ?? "time" });
        setHidden(new Set((d?.displayItems ?? "").split(",").filter(Boolean)));
        setCols(orderColumns(d?.displayOrder));
      })
      .catch(() => toast.error("データの取得に失敗しました。", toastOptions));
  }, []);

  const toggle = (key: string) => {
    if (ALWAYS_SHOWN.includes(key)) return;   // 台目・端末名 は非活性
    setHidden((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // 選択中の項目を index=to へ移動する（先頭/上/下/最後 共通）。
  const moveTo = (to: number) => {
    setCols((prev) => {
      const i = prev.findIndex((c) => c.key === selected);
      if (i < 0 || to < 0 || to >= prev.length || to === i) return prev;
      const n = [...prev];
      const [item] = n.splice(i, 1);
      n.splice(to, 0, item);
      return n;
    });
  };
  const selIdx = cols.findIndex((c) => c.key === selected);
  const canUp = selected !== null && selIdx > 0;
  const canDown = selected !== null && selIdx >= 0 && selIdx < cols.length - 1;

  // SKY-77: 初期状態＝全項目表示 かつ 既定の並び順。
  const reset = () => { setHidden(new Set()); setCols([...BOARD_COLUMNS]); setSelected(null); };

  const save = () => {
    // 台目・端末名 は常に表示のため非表示リストに含めない。
    const displayItems = cols.filter((c) => hidden.has(c.key) && !ALWAYS_SHOWN.includes(c.key)).map((c) => c.key).join(",");
    const displayOrder = cols.map((c) => c.key).join(",");   // SKY-77
    axiosInstance
      .put(HOST() + "/Form/V1/Dat/AsphaltDashboard/Setting", { ...keep, displayItems, displayOrder })
      .then(() => toast.success("更新に成功。", toastOptions))
      .catch(() => toast.error("更新に失敗。", toastOptions));
  };

  return (
    <div className="asp-dashboard">
      {/* SKY-50: 表示項目設定を画面中央に配置（見本準拠）。 */}
      <div style={{ width: "560px", maxWidth: "100%", margin: "0 auto" }}>
        <div className="asp-devhead">
          <div className="asp-title" style={{ flex: 1 }}>表示項目設定</div>
          <div>
            <button type="button" className="btn btn-primary btn-sm mar-5" onClick={() => (window.location.href = "/dashboard")}>キャンセル</button>
            <button type="button" className="btn btn-primary btn-sm mar-5" onClick={save}>保存</button>
          </div>
        </div>

        {/* SKY-77: ぴたあっとWeb端末一覧表示項目設定と同じレイアウト。「初期状態に戻す」は表の上、
            並べ替え(先頭/ひとつ上/ひとつ下/最後)は表の右に縦並びのアイコンボタンで配置する。 */}
        <div style={{ margin: "8px 0" }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={reset}>初期状態に戻す</button>
        </div>

        <div style={{ display: "flex" }}>
          <div className="asp-board-wrap" style={{ flex: 1 }}>
            <table className="asp-board asp-device" style={{ width: "100%", minWidth: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>表示</th>
                  <th>項目名</th>
                </tr>
              </thead>
              <tbody>
                {cols.map((c) => (
                  <tr
                    key={c.key}
                    className={selected === c.key ? "asp-row-selected" : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelected(c.key)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={ALWAYS_SHOWN.includes(c.key) || !hidden.has(c.key)}
                        disabled={ALWAYS_SHOWN.includes(c.key)}
                        onChange={() => toggle(c.key)}
                      />
                    </td>
                    <td>{c.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="asp-btn-move">
            <button type="button" className="btn btn-secondary btn-sm" disabled={!canUp} onClick={() => moveTo(0)} title="先頭に移動">
              <FontAwesomeIcon icon={faAngleDoubleUp} size="2x" color="#6f7175" />
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!canUp} onClick={() => moveTo(selIdx - 1)} title="ひとつ上に移動">
              <FontAwesomeIcon icon={faAngleUp} size="2x" color="#6f7175" />
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!canDown} onClick={() => moveTo(selIdx + 1)} title="ひとつ下に移動">
              <FontAwesomeIcon icon={faAngleDown} size="2x" color="#6f7175" />
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!canDown} onClick={() => moveTo(cols.length - 1)} title="最後に移動">
              <FontAwesomeIcon icon={faAngleDoubleDown} size="2x" color="#6f7175" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayItemSetting;
